import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, LucideIcon } from "lucide-react";
import { Article } from "@/lib/types";
import { timeAgo } from "@/lib/api";
import { useArticlePath } from "@/lib/i18n";
import { COIN_META, track } from "./navConfig";

export const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/60";

export const REGION = "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-200 motion-safe:fill-mode-both";
export const delay = (i: number) => ({ animationDelay: `${i * 25}ms` });

export const Micro = ({ children, cyan, className = "" }: { children: ReactNode; cyan?: boolean; className?: string }) => (
  <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${cyan ? "text-[#00E5FF]" : "text-[#A78BFA]"} ${className}`}>
    {children}
  </p>
);

export const LiveDot = () => (
  <span className="relative inline-flex h-1.5 w-1.5 ml-1.5 align-middle">
    <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22C55E] opacity-60" />
    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#22C55E]" />
  </span>
);

export const PanelCTA = ({ to, children, testid }: { to: string; children: ReactNode; testid?: string }) => (
  <Link to={to} data-testid={testid} onClick={() => track("nav_click", { to })}
    className={`group/cta inline-flex items-center gap-1.5 text-[13px] font-bold text-[#00E5FF] hover:text-white transition-colors rounded ${FOCUS}`}>
    {children}
    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/cta:translate-x-1" />
  </Link>
);

export const NavRow = ({ to, icon: Icon, label, desc, testid }: {
  to: string; icon: LucideIcon; label: string; desc: string; testid?: string;
}) => (
  <Link to={to} data-testid={testid} onClick={() => track("nav_click", { to })}
    className={`group/row flex items-start gap-3 rounded-lg px-2.5 py-2 -mx-2.5 hover:bg-white/[0.04] transition-colors ${FOCUS}`}>
    <span className="mt-0.5 h-8 w-8 shrink-0 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center text-gray-500 group-hover/row:text-[#00E5FF] group-hover/row:border-[#00E5FF]/30 transition-colors">
      <Icon className="h-4 w-4" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-gray-200 group-hover/row:text-white transition-colors">{label}</span>
      <span className="block text-xs text-gray-500 leading-snug">{desc}</span>
    </span>
    <ArrowUpRight aria-hidden className="h-3.5 w-3.5 shrink-0 mt-1.5 text-[#00E5FF] opacity-0 -translate-x-1 group-hover/row:opacity-100 group-hover/row:translate-x-0 motion-safe:transition-all motion-safe:duration-200" />
  </Link>
);

export const TagChip = ({ children }: { children: ReactNode }) => (
  <span className="font-mono text-[9px] uppercase tracking-wider border border-white/15 text-gray-500 rounded px-1.5 py-px">
    {children}
  </span>
);

export const StoryRow = ({ a, rank, testid }: { a: Article; rank?: number; testid?: string }) => {
  const toArticle = useArticlePath();
  return (
    <Link to={toArticle(a.slug)} data-testid={testid} onClick={() => track("nav_click", { story: a.slug })}
      className={`group/story flex items-start gap-3 rounded-lg border border-transparent p-2 -mx-2 hover:bg-white/[0.05] hover:border-white/[0.08] transition-colors duration-200 ${FOCUS}`}>
      {rank !== undefined && (
        <span className="font-mono text-[10px] font-bold leading-none text-white/25 group-hover/story:text-[#00E5FF]/50 w-5 shrink-0 pt-2 transition-colors">
          {String(rank).padStart(2, "0")}
        </span>
      )}
      {a.hero_image_url && (
        <span className="block h-12 w-[72px] shrink-0 rounded-md overflow-hidden border border-white/10">
          <img src={a.hero_image_url} alt="" loading="lazy"
            className="h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-200 group-hover/story:scale-[1.02]" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block font-news text-[13px] font-semibold text-gray-200 group-hover/story:text-white leading-snug line-clamp-2 transition-colors">
          {a.title}
        </span>
        <span className="mt-1 flex items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-wider text-gray-600">{timeAgo(a.published_at)}</span>
          {(a.categories?.length ?? 0) > 0 && (
            <TagChip>{(a.categories![0] === "government-policy" ? "policy" : a.categories![0])}</TagChip>
          )}
        </span>
      </span>
      <ArrowUpRight aria-hidden className="h-3.5 w-3.5 shrink-0 mt-1 text-[#00E5FF] opacity-0 -translate-x-1 group-hover/story:opacity-100 group-hover/story:translate-x-0 motion-safe:transition-all motion-safe:duration-200" />
    </Link>
  );
};

export const CoinIcon = ({ symbol, size = 28 }: { symbol: string; size?: number }) => {
  const m = COIN_META[symbol] || { color: "#666", glyph: symbol[0] };
  return (
    <span aria-hidden className="inline-flex items-center justify-center rounded-full font-mono font-bold shrink-0"
      style={{ height: size, width: size, fontSize: size * 0.5, color: m.color, background: `${m.color}1f`, border: `1px solid ${m.color}55` }}>
      {m.glyph}
    </span>
  );
};

export const Pct = ({ v, className = "" }: { v: number | null | undefined; className?: string }) => {
  if (v === null || v === undefined) return <span className={`font-mono text-xs text-gray-600 ${className}`}>—</span>;
  const up = v >= 0;
  return (
    <span className={`font-mono text-xs ${up ? "text-[#22C55E]" : "text-[#EF4444]"} ${className}`}>
      {up ? "+" : ""}{v.toFixed(2)}%
    </span>
  );
};

export const fmtPrice = (p: number) =>
  "$" + (p >= 1000
    ? p.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : p.toLocaleString(undefined, { maximumFractionDigits: p >= 1 ? 2 : 4 }));

export const fmtBig = (n: number | null | undefined) => {
  if (!n) return "—";
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  return `$${(n / 1e6).toFixed(0)}M`;
};

export const Spark = ({ points, up, className = "w-16 h-6" }: { points: number[]; up: boolean; className?: string }) => {
  if (!points || points.length < 2) return <span className={`${className} inline-block`} />;
  const min = Math.min(...points), max = Math.max(...points);
  const span = max - min || 1;
  const pts = points.map((p, i) =>
    `${(i / (points.length - 1)) * 64},${22 - ((p - min) / span) * 20}`).join(" ");
  const color = up ? "#22C55E" : "#EF4444";
  return (
    <svg viewBox="0 0 64 24" className={`${className} shrink-0`} aria-hidden>
      <polyline pathLength={1} className="nl-spark-line" points={pts} fill="none" stroke={color}
        strokeWidth="1.5" strokeOpacity="0.85" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const GAUGE_COLORS: Record<string, string> = {
  "Extreme Fear": "#DC2626", Fear: "#F97316", Neutral: "#A3A3A3", Greed: "#84CC16", "Extreme Greed": "#22C55E",
};

const RING_A = [124, 58, 237];
const RING_B = [0, 229, 255];
const ringColor = (t: number) =>
  `rgb(${RING_A.map((a, i) => Math.round(a + (RING_B[i] - a) * t)).join(",")})`;

export const MiniRing = ({ value, label }: { value: number; label: string }) => {
  const N = 30;
  const lit = Math.max(1, Math.round((value / 100) * N));
  const color = GAUGE_COLORS[label] || "#A3A3A3";
  return (
    <div className="flex items-center gap-3.5" data-testid="latest-mini-ring">
      <div className="relative h-[68px] w-[68px] shrink-0">
        <svg viewBox="0 0 68 68" className="h-full w-full" aria-hidden>
          {Array.from({ length: N }, (_, i) => {
            const a = ((120 + (i / (N - 1)) * 300) * Math.PI) / 180;
            const cos = Math.cos(a), sin = Math.sin(a);
            const on = i < lit;
            return (
              <line key={i}
                x1={34 + 24.5 * cos} y1={34 + 24.5 * sin}
                x2={34 + 31 * cos} y2={34 + 31 * sin}
                stroke={on ? ringColor(i / (N - 1)) : "rgba(255,255,255,0.08)"}
                strokeWidth={on ? 2.6 : 1.8} strokeLinecap="round" />
            );
          })}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-mono font-bold text-xl text-white">
          {value}
        </span>
      </div>
      <div className="min-w-0">
        <p className="font-news font-bold text-sm leading-tight" style={{ color }}>{label}</p>
        <div className="mt-1.5">
          <PanelCTA to="/layer-index" testid="latest-layer-index-cta">Layer Index</PanelCTA>
        </div>
      </div>
    </div>
  );
};

export const SkRow = ({ thumb = true }: { thumb?: boolean }) => (
  <div className="flex items-start gap-3 p-2 -mx-2" aria-hidden>
    {thumb && <div className="h-12 w-[72px] rounded-md bg-white/[0.05] motion-safe:animate-pulse shrink-0" />}
    <div className="flex-1 space-y-2 pt-0.5">
      <div className="h-3 rounded bg-white/[0.06] motion-safe:animate-pulse" />
      <div className="h-3 w-2/3 rounded bg-white/[0.05] motion-safe:animate-pulse" />
    </div>
  </div>
);

export const GhostWord = ({ word }: { word: string }) => (
  <span aria-hidden
    className="pointer-events-none select-none absolute right-2 -bottom-7 font-heading font-black uppercase text-[110px] leading-none tracking-tighter text-white/[0.012]">
    {word}
  </span>
);
